import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import AuthCallback from './pages/AuthCallback';
import { OAuth2Callback } from './components/OAuth';
import Workspaces from './pages/Workspaces';
import InvitePage from './pages/InvitePage';
import { useAuthenticator } from '@aws-amplify/ui-react';
import GanttPlayground from './pages/sandbox/GanttPlayground';

// Workspace pages — new function-based layout
import WorkspaceLayout from './layouts/WorkspaceLayout';
import DashboardPage from './pages/workspace/DashboardPage';
import YourWorkPage from './pages/workspace/YourWorkPage';
import YourInterestsPage from './pages/workspace/YourInterestsPage';
import YourTeamsPage from './pages/workspace/YourTeamsPage';
import DataExplorePage from './pages/workspace/DataExplorePage';
import WorkspaceHubPage from './pages/workspace/WorkspaceHubPage';
import InvitationsPage from './pages/workspace/InvitationsPage';
import ToolsPage from './pages/workspace/ToolsPage';
import ProfilePage from './pages/workspace/ProfilePage';

// Detail pages (flat paths preserved)
import TaskDetailPage from './pages/workspace/TaskDetailPage';
import PersonDetailPage from './pages/workspace/PersonDetailPage';
import TeamDetailPage from './pages/workspace/TeamDetailPage';
import OutcomeDetailPage from './pages/workspace/OutcomeDetailPage';
import ProjectDetailPage from './pages/workspace/ProjectDetailPage';
import DecisionDetailPage from './pages/workspace/DecisionDetailPage';
import ProblemDetailPage from './pages/workspace/ProblemDetailPage';
import ProductDetailPage from './pages/workspace/ProductDetailPage';
import StakeholderDetailPage from './pages/workspace/StakeholderDetailPage';
import WorkflowInstanceDetailPage from './pages/workspace/WorkflowInstanceDetailPage';
import ResourceDetailPage from './pages/workspace/ResourceDetailPage';
import ResourceGroupDetailPage from './pages/workspace/ResourceGroupDetailPage';

// Data/graph views
import DependenciesPage from './pages/workspace/DependenciesPage';
import { PrioritiesPage } from './pages/workspace/PrioritiesPage';

// Workspace admin pages
import SettingsPage from './pages/workspace/SettingsPage';
import DataSourcesPage from './pages/workspace/DataSourcesPage';
import DataSourceImportPage from './pages/workspace/DataSourceImportPage';
import ChangesListPage from './pages/workspace/ChangesListPage';
import PendingChangesPage from './pages/workspace/PendingChangesPage';

// Analysis tools
import ConsolidationPage from './pages/workspace/ConsolidationPage';
import TidyPage from './pages/workspace/TidyPage';

// AI assistant
import AssistantPage from './pages/workspace/AssistantPage';

// Workflows (under Data)
import WorkflowsPage from './pages/workspace/WorkflowsPage';
import WorkflowTemplateEditor from './pages/workspace/WorkflowTemplateEditor';

// Teams (list + detail handled by existing pages)
import TeamsPage from './pages/workspace/TeamsPage';

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, authStatus } = useAuthenticator(ctx => {
    return [ctx.user, ctx.authStatus];
  });

  // Still initializing — show spinner
  if (authStatus === 'configuring') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cm-bg-deep">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cm-accent-orange"></div>
      </div>
    );
  }

  // Not authenticated — redirect to sign-in
  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Router configuration
const router = createBrowserRouter([
  // ─── Public routes ───────────────────────────────────────────
  {
    path: '/',
    element: <Home />,
  },
  {
    path: '/auth/signin',
    element: <SignIn />,
  },
  {
    path: '/auth/signup',
    element: <SignUp />,
  },
  {
    path: '/auth/callback/cognito',
    element: <AuthCallback />,
  },
  {
    path: '/sandbox/gantt',
    element: <GanttPlayground />,
  },
  {
    path: '/oauth2/callback/:provider',
    element: <OAuth2Callback />,
  },
  {
    path: '/invite/:tokenId',
    element: <InvitePage />,
  },

  // ─── Protected routes ────────────────────────────────────────
  {
    path: '/workspaces',
    element: (
      <ProtectedRoute>
        <Workspaces />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workspaces/:workspaceId',
    element: (
      <ProtectedRoute>
        <WorkspaceLayout />
      </ProtectedRoute>
    ),
    children: [
      // Index → dashboard
      {
        index: true,
        element: <Navigate to="dashboard" replace />
      },

      // ─── Function-based pages ─────────────────────────────
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'work',
        element: <YourWorkPage />,
      },
      {
        path: 'teams',
        element: <YourTeamsPage />,
      },
      {
        path: 'teams/:teamId',
        element: <TeamDetailPage />,
      },
      {
        path: 'interests',
        element: <YourInterestsPage />,
      },
      {
        path: 'assistant',
        element: <AssistantPage />,
      },

      // ─── Data section ─────────────────────────────────────
      {
        path: 'data',
        element: <Navigate to="explore" replace />,
      },
      {
        path: 'data/explore',
        element: <DataExplorePage />,
      },
      {
        path: 'data/workflows',
        element: <WorkflowsPage />,
      },
      {
        path: 'data/workflows/templates/new',
        element: <WorkflowTemplateEditor />,
      },
      {
        path: 'data/workflows/templates/:templateId/edit',
        element: <WorkflowTemplateEditor />,
      },

      // ─── Detail pages (flat paths) ───────────────────────
      {
        path: 'tasks/:taskId',
        element: <TaskDetailPage />,
      },
      {
        path: 'people/:personId',
        element: <PersonDetailPage />,
      },
      {
        path: 'projects/:projectId',
        element: <ProjectDetailPage />,
      },
      {
        path: 'outcomes/:outcomeId',
        element: <OutcomeDetailPage />,
      },
      {
        path: 'decisions/:decisionId',
        element: <DecisionDetailPage />,
      },
      {
        path: 'problems/:problemId',
        element: <ProblemDetailPage />,
      },
      {
        path: 'products/:productId',
        element: <ProductDetailPage />,
      },
      {
        path: 'stakeholders/:stakeholderId',
        element: <StakeholderDetailPage />,
      },
      {
        path: 'workflow-instances/:instanceId',
        element: <WorkflowInstanceDetailPage />,
      },
      {
        path: 'resources/:resourceId',
        element: <ResourceDetailPage />,
      },
      {
        path: 'resource-groups/:groupId',
        element: <ResourceGroupDetailPage />,
      },

      // ─── Graph viewers ────────────────────────────────────
      {
        path: 'dependencies',
        element: <DependenciesPage />,
      },
      {
        path: 'priorities',
        element: <PrioritiesPage />,
      },

      // ─── Workspace admin ──────────────────────────────────
      {
        path: 'workspace',
        element: <WorkspaceHubPage />,
      },
      {
        path: 'workspace/settings',
        element: <SettingsPage />,
      },
      {
        path: 'workspace/data-sources',
        element: <DataSourcesPage />,
      },
      {
        path: 'workspace/data-sources/:dataSourceId/import',
        element: <DataSourceImportPage />,
      },
      {
        path: 'workspace/invitations',
        element: <InvitationsPage />,
      },
      {
        path: 'workspace/pending-changes',
        element: <PendingChangesPage />,
      },
      {
        path: 'workspace/history',
        element: <ChangesListPage />,
      },
      {
        path: 'workspace/tools',
        element: <ToolsPage />,
      },
      {
        path: 'workspace/tools/consolidation',
        element: <ConsolidationPage />,
      },
      {
        path: 'workspace/tools/tidy',
        element: <TidyPage />,
      },

      // ─── User profile ────────────────────────────────────
      {
        path: 'profile',
        element: <ProfilePage />,
      },

      // ─── Legacy redirects for hidden routes ───────────────
      {
        path: 'pending-changes',
        element: <Navigate to="../workspace/pending-changes" replace />,
      },

      // ─── Legacy redirects ────────────────────────────────
      // Old routes that redirect to new locations
      {
        path: 'schedule',
        element: <Navigate to="../dashboard" replace />,
      },
      {
        path: 'tasks',
        element: <Navigate to="../data/explore" replace />,
      },
      {
        path: 'people',
        element: <Navigate to="../data/explore" replace />,
      },
      {
        path: 'outcomes',
        element: <Navigate to="../data/explore" replace />,
      },
      {
        path: 'decisions',
        element: <Navigate to="../data/explore" replace />,
      },
      {
        path: 'stakeholders',
        element: <Navigate to="../data/explore" replace />,
      },
      {
        path: 'workflow-instances',
        element: <Navigate to="../data/explore" replace />,
      },
      {
        path: 'workflows',
        element: <Navigate to="../data/workflows" replace />,
      },
      {
        path: 'workflows/templates/new',
        element: <Navigate to="../data/workflows/templates/new" replace />,
      },
      {
        path: 'workflows/templates/:templateId/edit',
        element: <WorkflowTemplateEditor />,
      },
      {
        path: 'settings',
        element: <Navigate to="../workspace/settings" replace />,
      },
      {
        path: 'data-sources',
        element: <Navigate to="../workspace/data-sources" replace />,
      },
      {
        path: 'data-sources/:dataSourceId/import',
        element: <DataSourceImportPage />,
      },
      {
        path: 'changes',
        element: <Navigate to="../workspace/history" replace />,
      },
      {
        path: 'consolidation',
        element: <Navigate to="../workspace/tools/consolidation" replace />,
      },
      {
        path: 'tidy',
        element: <Navigate to="../workspace/tools/tidy" replace />,
      },
    ]
  },
]);

export const Router = () => {
  return <RouterProvider router={router} />;
};

export default Router;
